import React, { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { ScoreRadial } from "@/components/ScoreRadial";

/** First real lines of step 4 (Market) for this project, progressively blurred behind the paywall. */
export function MarketTeaser({ projectId, onUnlock }) {
  const { t } = useLang();
  const [teaser, setTeaser] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setState("loading");
    api.get(`/projects/${projectId}/teaser`)
      .then(({ data }) => { if (alive) { setTeaser(data); setState("ok"); } })
      .catch(() => { if (alive) setState("error"); });
    return () => { alive = false; };
  }, [projectId]);

  if (state === "error") return null;

  return (
    <div className="mt-8 rounded-3xl border border-slate-200 bg-white overflow-hidden text-left" data-testid="market-teaser">
      <div className="px-7 pt-6 flex items-center gap-3 flex-wrap">
        <span className="cpenz-overline">{t("flow.teaser_overline")}</span>
        <span className="text-xs font-semibold text-slate-400">{t("flow.teaser_step")}</span>
      </div>
      {state === "loading" ? (
        <div className="px-7 py-8 flex items-center gap-3 text-slate-400 text-sm" data-testid="market-teaser-loading">
          <Loader2 size={16} className="animate-spin text-[#7c3aed]" /> {t("flow.teaser_loading")}
        </div>
      ) : (
        <div className="relative px-7 pt-4 pb-7">
          {teaser.provisional_score && (
            <div className="mb-6 rounded-2xl bg-[#f4f6fa] p-5 flex flex-col sm:flex-row items-center gap-5" data-testid="provisional-score">
              <div className="shrink-0 pb-4"><ScoreRadial total={teaser.provisional_score.total} band={teaser.provisional_score.band} size={132} /></div>
              <div className="flex-1 w-full">
                <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7c3aed]">{t("flow.pscore_title")}</p>
                <p className="mt-1 text-sm text-slate-500">{t("flow.pscore_sub")}</p>
                <div className="mt-3 space-y-2">
                  {[["clarity", t("flow.pscore_clarity")], ["problem", t("flow.pscore_problem")], ["customer", t("flow.pscore_customer")]].map(([k, label]) => (
                    <div key={k} className="flex items-center gap-3" data-testid={`pscore-${k}`}>
                      <span className="w-24 text-xs font-semibold text-slate-500">{label}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-white overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b82f6]" style={{ width: `${teaser.provisional_score[k]}%` }} /></div>
                      <span className="w-7 text-right text-xs font-bold text-[#0b0f1a]">{teaser.provisional_score[k]}</span>
                    </div>
                  ))}
                </div>
                {teaser.provisional_score.missing && <p className="mt-3 text-xs text-slate-400 leading-relaxed" data-testid="pscore-missing">{teaser.provisional_score.missing}</p>}
              </div>
            </div>
          )}
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0b0f1a] leading-snug" data-testid="market-teaser-headline">{teaser.headline}</h3>
          <div className="relative mt-4">
            <p className="text-base text-slate-600 leading-relaxed" data-testid="market-teaser-visible">{teaser.paragraphs[0]}</p>
            <div className="relative mt-3 select-none pointer-events-none max-h-[150px] sm:max-h-[170px] overflow-hidden" aria-hidden="true" data-testid="market-teaser-blurred">
              {teaser.paragraphs.slice(1, 2).map((p, i) => (
                <p key={i} className="mt-3 text-base text-slate-600 leading-relaxed" style={{ filter: "blur(5px)", opacity: 0.7 }}>{p}</p>
              ))}
              <div className="absolute inset-x-0 bottom-0 h-full bg-gradient-to-b from-transparent via-white/80 to-white" />
            </div>
            <div className="mt-2 flex flex-col items-center text-center">
              <button
                type="button"
                onClick={onUnlock}
                data-testid="market-teaser-unlock"
                className="w-full sm:w-auto whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-full bg-[#0b0f1a] text-white text-[15px] sm:text-base font-semibold px-6 sm:px-8 min-h-[52px] shadow-[0_18px_40px_rgba(11,15,26,0.28)] hover:bg-black hover:-translate-y-0.5 transition-[transform,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed] focus-visible:ring-offset-2"
              >
                <Lock size={15} className="text-[#a78bfa]" /> {t("flow.teaser_cta")} →
              </button>
              <p className="mt-3 text-xs text-slate-500" data-testid="market-teaser-cta-hint">{t("flow.teaser_cta_hint")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
